/**
 * economy.ts — the ore market (mean-reverting price walk) plus selling.
 */
import type { GameState } from '../core/GameState';
import type { EventBus } from '../core/EventBus';
import { safeNumber, clamp } from '../core/numbers';
import { normal } from '../core/rng';
import { BALANCE, ORES, ORE_BY_ID } from '../data';
import type { Derived } from './derived';
import { computeMultipliers } from './modifiers';

/** Advance market prices via a seeded Ornstein–Uhlenbeck-style walk. */
export function tickMarket(state: GameState, _dtMs: number): void {
  if (state.meta.gameTimeMs < state.market.nextUpdateAt) return;
  state.market.nextUpdateAt = state.meta.gameTimeMs + BALANCE.market.updateInterval * 1000;

  const { reversion, volatility, minFactor, maxFactor } = BALANCE.market;
  for (const ore of ORES) {
    const base = ore.baseValue;
    const price = state.market.price[ore.id] ?? base;
    const drift = reversion * (base - price);
    const shock = volatility * base * normal(state.rng);
    const next = clamp(price + drift + shock, base * minFactor, base * maxFactor);
    state.market.price[ore.id] = safeNumber(next, base);
  }
}

/** Current effective sale price of one raw unit (price × value mult × global). */
export function unitPrice(state: GameState, oreId: string, d?: Derived): number {
  const mults = d?.mults ?? computeMultipliers(state);
  const price = state.market.price[oreId] ?? ORE_BY_ID[oreId]?.baseValue ?? 0;
  return safeNumber(price * mults.value * mults.global);
}

function credit(state: GameState, oreId: string, amount: number, value: number): void {
  state.economy.money += value;
  state.stats.lifetimeEarnings += value;
  state.stats.oreSold[oreId] = (state.stats.oreSold[oreId] ?? 0) + amount;
}

/** Sell raw ore. Returns money earned. */
export function sellOre(state: GameState, oreId: string, amount: number, d?: Derived, bus?: EventBus): number {
  const have = state.mining.ore[oreId] ?? 0;
  const qty = Math.min(amount, have);
  if (qty <= 0) return 0;
  const value = qty * unitPrice(state, oreId, d);
  state.mining.ore[oreId] = have - qty;
  credit(state, oreId, qty, value);
  bus?.emit('sell', { oreId, amount: qty, value });
  return value;
}

/** Sell refined ingots (worth ingotValueMult × the raw price). Returns money earned. */
export function sellIngots(state: GameState, oreId: string, amount: number, d?: Derived, bus?: EventBus): number {
  const have = state.mining.ingots[oreId] ?? 0;
  const qty = Math.min(amount, have);
  if (qty <= 0) return 0;
  const value = qty * unitPrice(state, oreId, d) * BALANCE.ingotValueMult;
  state.mining.ingots[oreId] = have - qty;
  credit(state, oreId, qty, value);
  bus?.emit('sell', { oreId, amount: qty, value });
  return value;
}

/** Sell everything in stock. Returns total money earned. */
export function sellAll(state: GameState, d?: Derived, bus?: EventBus): number {
  let total = 0;
  for (const ore of ORES) {
    total += sellOre(state, ore.id, state.mining.ore[ore.id] ?? 0, d, bus);
    total += sellIngots(state, ore.id, state.mining.ingots[ore.id] ?? 0, d, bus);
  }
  return total;
}

/** Auto-sell tick: liquidate stock each tick when enabled. */
export function tickAutosell(state: GameState, d: Derived): void {
  if (!state.settings.autosell) return;
  sellAll(state, d);
}
